// Data Import Pipeline types — matches backend snake_case schema (spec §3.1A).

export type SourceType =
  | 'csv'
  | 'xlsx'
  | 'vcf'
  | 'linkedin_export'
  | 'business_cards';

export type BatchStatus =
  | 'pending'
  | 'parsing'
  | 'mapping'
  | 'preview'
  | 'importing'
  | 'queuing_research'
  | 'complete'
  | 'failed';

export type BusinessCardStatus =
  | 'pending'
  | 'processing'
  | 'extracted'
  | 'review_needed'
  | 'imported'
  | 'failed';

export interface ImportBatch {
  id: number;
  source_file?: string;
  source_type: SourceType;
  status: BatchStatus;

  total_records: number;
  valid_records: number;
  duplicate_records: number;
  error_records: number;
  imported_records: number;
  research_queued: number;
  research_complete: number;

  field_mapping?: Record<string, string>;
  tags?: string[];
  research_priority?: number;
  estimated_completion?: string;
  error_log?: Array<Record<string, any>>;
  target_type?: 'investor' | 'company';
  group_name?: string;

  created_at: string;
  updated_at?: string;
  created_by?: string;
}

export interface ImportBatchList {
  batches: ImportBatch[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface BusinessCardImage {
  id: number;
  batch_id: number;
  image_url?: string;
  image_filename?: string;
  extracted_data?: any; // dict or list[dict]
  extraction_confidence?: number;
  investor_id?: number;
  status: BusinessCardStatus;
  error_message?: string;
  created_at: string;
}

export interface DedupMatch {
  existing_id: number;
  existing_name?: string;
  confidence: number;
  action: 'merge' | 'review';
}

export interface PreviewRecord {
  row: number;
  record: Record<string, any>;
  dedup_matches: DedupMatch[];
  suggested_action: 'create' | 'merge' | 'skip' | 'review';
  errors: string[];
}

export interface PreviewResponse {
  batch_id: number;
  total: number;
  create_count: number;
  merge_count: number;
  review_count: number;
  records: PreviewRecord[];
}

export interface ParseMappingResponse {
  batch_id: number;
  columns: string[];
  suggested_mapping: Record<string, string>;
  total_records: number;
  sample: Record<string, any>[];
}

export interface CommitRequest {
  tags?: string[];
  research_priority?: number;
  dedup_actions?: Record<string, 'create' | 'merge' | 'skip'>;
  skip_research?: boolean;
  group_name?: string;
}

export interface CommitResponse {
  batch_id: number;
  imported_records: number;
  research_queued: number;
  errors: number;
  investor_ids: number[];
}

export interface BatchStatusResponse extends Partial<ImportBatch> {
  id: number;
  status: BatchStatus;
}

export interface BusinessCardReviewRequest {
  action: 'accept' | 'edit' | 'reject';
  extracted_data?: Record<string, any>;
}

// Target investor fields for the mapping UI.
export const INVESTOR_TARGET_FIELDS: Array<{ value: string; label: string }> = [
  { value: 'full_name', label: 'Full Name' },
  { value: 'firm_name', label: 'Firm Name' },
  { value: 'title', label: 'Title' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'linkedin_url', label: 'LinkedIn URL' },
  { value: 'website_url', label: 'Website' },
  { value: 'location_city', label: 'City' },
  { value: 'location_state', label: 'State' },
  { value: 'location_country', label: 'Country' },
  { value: 'investor_type', label: 'Investor Type' },
  { value: 'sector_focus', label: 'Sector Focus (comma-sep)' },
  { value: 'stage_preferences', label: 'Stage Preferences (comma-sep)' },
  { value: 'check_size_min', label: 'Check Size Min' },
  { value: 'check_size_max', label: 'Check Size Max' },
  { value: 'tags', label: 'Tags (comma-sep)' },
  { value: 'notes', label: 'Notes' },
  { value: 'mission_areas', label: 'Mission Areas (philanthropy)' },
  { value: 'grant_size_min', label: 'Grant Size Min' },
  { value: 'grant_size_max', label: 'Grant Size Max' },
  { value: 'ein', label: 'EIN' },
];

// Target company fields for the mapping UI. Used when the import
// batch's target_type is "company" — the dropdown surfaces all
// company-side fields the import pipeline accepts.
export const COMPANY_TARGET_FIELDS: Array<{ value: string; label: string }> = [
  { value: 'name', label: 'Company Name' },
  { value: 'website', label: 'Website' },
  { value: 'industry', label: 'Industry' },
  { value: 'sub_industry', label: 'Sub-industry' },
  { value: 'description', label: 'Description' },
  { value: 'executive_summary', label: 'Executive Summary' },
  { value: 'headquarters', label: 'Headquarters' },
  { value: 'location_city', label: 'City' },
  { value: 'location_state', label: 'State' },
  { value: 'contact_email', label: 'Contact Email' },
  { value: 'contact_name', label: 'Contact Name' },
  { value: 'funding_stage', label: 'Funding Stage' },
  { value: 'funding_amount_sought', label: 'Amount Currently Raising' },
  { value: 'previous_funding_raw', label: 'Funding to Date' },
  { value: 'valuation', label: 'Valuation' },
  { value: 'current_revenue', label: 'Annual Revenue / ARR' },
  { value: 'monthly_revenue', label: 'Monthly Revenue' },
  { value: 'revenue_growth_rate', label: 'Revenue Growth Rate' },
  { value: 'team_size', label: 'Team Size' },
  { value: 'employee_count', label: 'Employee Count' },
  { value: 'pitch_deck_url', label: 'Pitch Deck (URL or Airtable)' },
  { value: 'founder_demographics', label: 'Founder Demographics' },
  { value: 'university_affiliations', label: 'University Affiliations' },
  { value: 'tags', label: 'Tags (comma-sep)' },
];
